import type {
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
} from "@tanstack/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { RefreshClient } from "@citrine/client";

import type {
  ModelExpertQuestionRequest,
  ModelExpertResponse,
} from "./model-expert-types";

export type DocumentMetadata =
  | {
      source_type: "doi" | "isbn" | "https";
      source: string;
      id?: never;
    }
  | {
      source_type: "private";
      id: string;
      source: string;
    };

export interface CatalystLiteratureAnswer {
  document_metadata: DocumentMetadata[];
  relevant_documents: boolean;
  response: string;
}

interface PredictionInsightsRequest {
  project_name: string;
  project_description: string;
  predictor_uid: string;
  version: string;
  property_name: string;
  performance_metrics: Record<string, any>;
  feature_importances: Record<string, number>;
}

interface PredictionInsightsResponse {
  uid: string;
  predictor_uid: string;
  version: string;
  property_name: string;
  response: string;
  created_on: number;
}

export class CatalystClient extends RefreshClient {
  private static instance: CatalystClient | undefined;

  protected constructor() {
    super();
  }

  static getInstance() {
    return (this.instance ??= new this());
  }

  askLiteratureQuestion(question?: string) {
    return this.request<CatalystLiteratureAnswer>({
      url: "/api/v1/catalyst/documents/knowledge-base/search",
      data: { question },
      method: "POST",
    });
  }

  useAskLiteratureQuestion(
    options?: Omit<
      UseMutationOptions<CatalystLiteratureAnswer, AxiosError, string>,
      "mutationFn"
    >,
  ): UseMutationResult<CatalystLiteratureAnswer, AxiosError, string> {
    return useMutation({
      ...options,
      mutationFn: (question) => this.askLiteratureQuestion(question),
    });
  }

  askModelQuestion(data: ModelExpertQuestionRequest) {
    return this.request<ModelExpertResponse>({
      url: "/api/v1/catalyst/model-expert/ai-action",
      data,
      method: "POST",
    });
  }

  useAskModelQuestion(
    options?: Omit<
      UseMutationOptions<
        ModelExpertResponse,
        AxiosError,
        ModelExpertQuestionRequest
      >,
      "mutationFn"
    >,
  ) {
    return useMutation({
      ...options,
      mutationFn: (question) => this.askModelQuestion(question),
    });
  }

  getPredictionInsights(predictionInsightsRequest: PredictionInsightsRequest) {
    return this.request<PredictionInsightsResponse>({
      method: "POST",
      url: "/api/v1/catalyst/prediction-insights",
      data: predictionInsightsRequest,
    });
  }

  useGetPredictionInsights(
    predictionInsightsRequest: PredictionInsightsRequest,
    options: Omit<
      UseQueryOptions<PredictionInsightsResponse>,
      "queryKey" | "queryFn"
    > = {},
  ) {
    const { predictor_uid, version, property_name } = predictionInsightsRequest;

    return useQuery({
      ...options,
      queryKey: ["prediction-insights", predictor_uid, version, property_name],
      queryFn: () => this.getPredictionInsights(predictionInsightsRequest),
      enabled: Boolean(predictor_uid && version && (options.enabled ?? true)),
      staleTime: Infinity,
    });
  }
}
